import { Field, TextInput, TextArea, SectionCard, ListAddBtn, ListRemoveBtn } from "./_shared";
import ImageUploadField from "./ImageUploadField";

// Convert common video URLs (watch, youtu.be, vimeo) to embed format
function toEmbedUrl(url) {
  if (!url) return "";
  const trimmed = url.trim();

  // YouTube: watch?v=ID or shorts/ID
  const ytWatch = trimmed.match(/youtube\.com\/(?:watch\?v=|shorts\/|live\/)([A-Za-z0-9_-]{6,})/);
  if (ytWatch) return `https://www.youtube.com/embed/${ytWatch[1]}`;

  // YouTube short link youtu.be/ID
  const ytShort = trimmed.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/);
  if (ytShort) return `https://www.youtube.com/embed/${ytShort[1]}`;

  // Vimeo: vimeo.com/ID
  const vimeo = trimmed.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;

  return trimmed; // already embed or other provider
}

function ytIdFromEmbed(url) {
  const m = (url || "").match(/youtube\.com\/embed\/([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
}

export default function EditVideoGallery({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const setVid = (i, key, v) => {
    const videos = [...(value.videos || [])];
    videos[i] = { ...videos[i], [key]: v };
    set("videos", videos);
  };
  const setUrl = (i, raw) => {
    // Auto-convert + auto-fill thumbnail if YouTube and thumb empty
    const embed = toEmbedUrl(raw);
    const videos = [...(value.videos || [])];
    const ytId = ytIdFromEmbed(embed);
    videos[i] = {
      ...videos[i],
      url: embed,
      thumbnail: !videos[i].thumbnail && ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : videos[i].thumbnail,
    };
    set("videos", videos);
  };
  const addVid = () =>
    set("videos", [...(value.videos || []), { url: "", title: "Novo vídeo", thumbnail: "" }]);
  const removeVid = (i) => set("videos", (value.videos || []).filter((_, idx) => idx !== i));

  return (
    <SectionCard title="Galeria de Vídeos">
      <div className="space-y-5">
        <Field label="Overline"><TextInput value={value.overline} onChange={(v) => set("overline", v)} testId="video-overline-input" /></Field>
        <Field label="Título"><TextInput value={value.title} onChange={(v) => set("title", v)} testId="video-title-input" /></Field>
        <Field label="Subtítulo"><TextArea value={value.subtitle} onChange={(v) => set("subtitle", v)} rows={2} testId="video-subtitle-input" /></Field>

        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs tracking-[0.2em] uppercase text-onyx/60">Vídeos</span>
            <ListAddBtn onClick={addVid} label="Adicionar vídeo" testId="video-add-video" />
          </div>
          <div className="space-y-4">
            {(value.videos || []).map((vid, i) => {
              const ytId = ytIdFromEmbed(vid.url);
              return (
                <div key={i} data-testid={`video-row-${i}`} className="border border-border p-4 space-y-3 relative">
                  <div className="absolute top-3 right-3">
                    <ListRemoveBtn onClick={() => removeVid(i)} testId={`video-remove-${i}`} />
                  </div>
                  <Field label="Título do vídeo">
                    <TextInput value={vid.title} onChange={(val) => setVid(i, "title", val)} testId={`video-title-${i}`} />
                  </Field>
                  <Field
                    label="URL do vídeo"
                    hint="Pode colar o link normal do YouTube (youtube.com/watch?v=…), youtu.be/… ou vimeo.com/… — a conversão é automática."
                  >
                    <TextInput
                      value={vid.url}
                      onChange={(val) => setUrl(i, val)}
                      testId={`video-url-${i}`}
                      placeholder="https://www.youtube.com/watch?v=…"
                    />
                  </Field>
                  {vid.url && (
                    <p className="text-xs text-onyx/40 break-all">
                      Embed: <span className="text-gold">{vid.url}</span>
                    </p>
                  )}
                  <ImageUploadField
                    label="Thumbnail (capa)"
                    value={vid.thumbnail}
                    onChange={(val) => setVid(i, "thumbnail", val)}
                    testId={`video-thumbnail-${i}`}
                  />
                  {ytId && !vid.thumbnail && (
                    <button
                      type="button"
                      onClick={() => setVid(i, "thumbnail", `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`)}
                      data-testid={`video-use-yt-thumb-${i}`}
                      className="text-xs underline text-gold hover:text-onyx"
                    >
                      Usar thumbnail do YouTube
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
